import { pathOr } from 'ramda'
import { AiSpacedRepetitionQuizGenerateResponseDto } from 'types'
import { QuizOption, QuizQuestion } from './quiz.types'
import { hasQuillDelta, parseQuillDelta } from './quizDelta'

const readCorrectOptionId = (question: Record<string, unknown>): string => {
  const snake = pathOr('', ['correct_option_id'], question)
  const camel = pathOr('', ['correctOptionId'], question)
  return String(snake || camel)
}

const normalizeOption = (
  raw: unknown,
  questionIndex: number,
  optionIndex: number
): QuizOption | null => {
  if (!raw || typeof raw !== 'object') {
    return null
  }

  const record = raw as Record<string, unknown>
  const id = String(pathOr('', ['id'], record))
  const text = String(pathOr('', ['text'], record))
  const text_delta = parseQuillDelta(pathOr(null, ['text_delta'], record))
  const explanation = String(pathOr('', ['explanation'], record))
  const explanation_delta = parseQuillDelta(
    pathOr(null, ['explanation_delta'], record)
  )

  if (!text && !hasQuillDelta(text_delta)) {
    return null
  }

  return {
    id: id || `q${questionIndex}-opt-${optionIndex}`,
    text,
    text_delta,
    explanation,
    explanation_delta
  }
}

const normalizeQuestion = (
  raw: unknown,
  index: number
): QuizQuestion | null => {
  if (!raw || typeof raw !== 'object') {
    return null
  }

  const record = raw as Record<string, unknown>
  const id = String(pathOr('', ['id'], record))
  const prompt = String(pathOr('', ['prompt'], record))
  const prompt_delta = parseQuillDelta(pathOr(null, ['prompt_delta'], record))
  const concept = String(pathOr('', ['concept'], record))
  const explanation = String(pathOr('', ['explanation'], record))
  const explanation_delta = parseQuillDelta(
    pathOr(null, ['explanation_delta'], record)
  )
  const optionsRaw = pathOr<unknown[]>([], ['options'], record)
  const correctOptionId = readCorrectOptionId(record)

  if (
    !id ||
    (!prompt && !hasQuillDelta(prompt_delta)) ||
    !optionsRaw.length ||
    !correctOptionId
  ) {
    return null
  }

  const options = optionsRaw
    .map((option, optionIndex) => normalizeOption(option, index, optionIndex))
    .filter((option): option is QuizOption => Boolean(option))

  if (!options.some(option => option.id === correctOptionId)) {
    return null
  }

  return {
    id,
    concept,
    prompt,
    prompt_delta,
    explanation,
    explanation_delta,
    correctOptionId,
    options
  }
}

export const mapAiSpacedRepetitionQuizResponseToQuestions = (
  body: unknown
): QuizQuestion[] => {
  if (Array.isArray(body)) {
    return body
      .map((item, index) => normalizeQuestion(item, index))
      .filter((question): question is QuizQuestion => Boolean(question))
  }

  const record = body as Partial<AiSpacedRepetitionQuizGenerateResponseDto>
  const questionsFromQuestionsKey = pathOr<unknown[]>(
    [],
    ['questions'],
    record as Record<string, unknown>
  )
  const questionsFromDataKey = pathOr<unknown[]>(
    [],
    ['data'],
    record as Record<string, unknown>
  )
  const questions = questionsFromQuestionsKey.length
    ? questionsFromQuestionsKey
    : questionsFromDataKey

  return questions
    ?.map((item, index) => normalizeQuestion(item, index))
    ?.filter((question): question is QuizQuestion => Boolean(question))
}
